import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import {
  Dropdown,
  Avatar,
  Input,
  AutoComplete,
  Switch,
  Typography,
  Layout,
} from "antd";
import {
  UserOutlined,
  SearchOutlined,
  MoonOutlined,
  QuestionCircleOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import _ from "lodash";

/**
 * Actions
 */
import { changeSearchTerm, getUrlAvatar } from "auth/actions/userActions";

/**
 * Components
 */
import { Logo } from "assets/icons";
import NotificationCenter from "../../NotificationCenter";
import SelectorUnit from "../SelectorUnit";
import { useThemeSwitch } from "../../ThemeToggle";
import "reactjs-popup/dist/index.css";
import "../styles.scss";

/**
 * Misc
 */
const { Text } = Typography;
const HELP_CENTER_URL = "http://doc.clm.agr.br/docs/intro";

function TopBar({ user, avatar, getUrlAvatar, changeSearchTerm, search }) {
  const navigate = useNavigate();
  const { isDarkMode, handleToggle } = useThemeSwitch();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const loadUserAvatar = async () => {
      if (user && user.sub) {
        await getUrlAvatar(user.sub);
      }
    };
    loadUserAvatar();
  }, [user]);

  const displayName = user?.name || user?.preferred_username || "Usuário";

  const menuItems = [
    {
      key: 'user-info',
      style: { padding: '8px 12px' },
      label: (
        <div style={{ lineHeight: 1.4 }}>
          <Text strong style={{ display: 'block' }}>{displayName}</Text>
          {user?.email && (
            <Text type="secondary" style={{ fontSize: 12 }}>{user.email}</Text>
          )}
        </div>
      ),
    },
    { type: 'divider' },
    {
      key: 'theme',
      icon: <MoonOutlined style={{ fontSize: 16 }} />,
      style: { margin: '4px 0', fontSize: 14 },
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24 }}>
          <span>Modo escuro</span>
          {/* pointerEvents none: o clique é tratado no item do menu, o Switch é só indicador */}
          <Switch
            size="small"
            checked={isDarkMode}
            style={{ pointerEvents: 'none' }}
            aria-label="Alternar tema escuro"
          />
        </div>
      ),
    },
    {
      key: 'help',
      icon: <QuestionCircleOutlined style={{ fontSize: 16, color: 'var(--cp-error)' }} />,
      style: { margin: '4px 0', fontSize: 14 },
      label: 'Central de ajuda',
    },
    { type: 'divider' },
    {
      key: 'logout',
      danger: true,
      icon: <LogoutOutlined style={{ fontSize: 16 }} />,
      style: { margin: '4px 0', fontSize: 14 },
      label: 'Sair',
    },
  ];

  const handleMenuClick = ({ key }) => {
    if (key === 'theme') {
      // alterna o tema sem fechar o dropdown
      handleToggle();
      return;
    }
    setMenuOpen(false);
    if (key === 'user-info') navigate("/perfil");
    if (key === 'help') window.open(HELP_CENTER_URL, "_blank", "noopener,noreferrer");
    if (key === 'logout') navigate("/logout");
  };

  return (
    <Fragment>
      {/* React Fragment to hold Header */}
      <Layout.Header className="custom-header">
        <div className="header-content">
          <div className="header-left">
            {/* Logo Section */}
            <div className="company-logo">
              {/* "/" redireciona (AuthRoute) para a primeira rota permitida */}
              <a href="/" title={process.env.REACT_APP_NAME}>
                <Logo style={{
                  height: 30,
                  display: 'block'
                }} />
              </a>
            </div>

            {/* Search Section */}
            <div className="search-container">
              <AutoComplete
                value={search}
                style={{ width: "100%" }}
                onChange={(value) => changeSearchTerm(value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (e.target.value.length === 0) return;
                    document.location.replace(`/resultados/pesquisa/formularios/${e.target.value}`);
                  }
                }}
              >
                <Input
                  placeholder="Pesquisar..."
                  prefix={<SearchOutlined />}
                />
              </AutoComplete>
            </div>
          </div>

          {/* Actions Section */}
          <div className="actions-container">
            {/* Unit Selector */}
            <div className="unit-wrapper">
              <SelectorUnit />
            </div>

            {/* Notification */}
            <div className="action-item">
              {Object.keys(user).length > 0 ? <NotificationCenter /> : null}
            </div>

            {/* Profile Dropdown */}
            <Dropdown
              menu={{ items: menuItems, onClick: handleMenuClick, style: { minWidth: 240, padding: '8px 0' } }}
              trigger={['click']}
              open={menuOpen}
              onOpenChange={setMenuOpen}
            >
              <div className="action-item">
                <a href={() => false} onClick={(e) => e.preventDefault()} style={{ display: 'flex', alignItems: 'center', color: 'inherit' }}>
                  {
                    avatar === null ?
                      <Avatar size="default" icon={<UserOutlined />} style={{ backgroundColor: 'var(--cp-brand)' }} /> :
                      <Avatar size="default" src={avatar} />
                  }
                  {/* Tiny down arrow for affordance */}
                </a>
              </div>
            </Dropdown>
          </div>
        </div>
      </Layout.Header>
    </Fragment >
  );
}

const mapStateToProps = ({ session, user }) => ({
  user: session.user,
  avatar: user.avatar,
  search: user.search,
});

const mapDispatchToProps = (dispatch) => ({
  getUrlAvatar: (userId) => dispatch(getUrlAvatar(userId)),
  changeSearchTerm: (term = "") => dispatch(changeSearchTerm(term)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TopBar);
